import React, { useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const TradingViewWidget = ({ asset, onPriceUpdate }) => {
  const { tt } = useLanguage();
  const widgetRef = useRef();
  const scriptRef = useRef();
  
  // РАСШИРЕННЫЙ маппинг для всех новых активов
  const getTradingViewSymbol = (asset) => {
    const symbolMapping = {
      // Основные валютные пары (FX)
      'EURUSD': 'FX:EURUSD',
      'GBPUSD': 'FX:GBPUSD', 
      'USDJPY': 'FX:USDJPY',
      'USDCHF': 'FX:USDCHF',
      'AUDUSD': 'FX:AUDUSD',
      'USDCAD': 'FX:USDCAD',
      'NZDUSD': 'FX:NZDUSD',
      
      // Минорные валютные пары  
      'EURGBP': 'FX:EURGBP',
      'EURJPY': 'FX:EURJPY',
      'GBPJPY': 'FX:GBPJPY',
      'EURCHF': 'FX:EURCHF',
      
      // Криптовалюты (Binance)
      'BTCUSD': 'BINANCE:BTCUSDT',
      'ETHUSD': 'BINANCE:ETHUSDT',
      'BNBUSD': 'BINANCE:BNBUSDT',
      'ADAUSD': 'BINANCE:ADAUSDT',
      'SOLUSD': 'BINANCE:SOLUSDT',
      'XRPUSD': 'BINANCE:XRPUSDT',
      'DOTUSD': 'BINANCE:DOTUSDT',
      
      // Драгметаллы и товары (TVC)
      'XAUUSD': 'TVC:GOLD',       // Золото
      'XAGUSD': 'TVC:SILVER',     // Серебро
      'WTIUSD': 'NYMEX:CL1!',     // Нефть WTI
      'XPDUSD': 'NYMEX:PA1!',     // Палладий
      'XPTUSD': 'NYMEX:PL1!',     // Платина
      
      // Топовые акции США (NASDAQ/NYSE)
      'AAPL': 'NASDAQ:AAPL',
      'MSFT': 'NASDAQ:MSFT',
      'GOOGL': 'NASDAQ:GOOGL',
      'AMZN': 'NASDAQ:AMZN',
      'TSLA': 'NASDAQ:TSLA', 
      'META': 'NASDAQ:META',
      'NVDA': 'NASDAQ:NVDA',
      'NFLX': 'NASDAQ:NFLX',
      'CVX': 'NYSE:CVX',
      
      // Акции Казахстана
      'KSPI': 'NASDAQ:KSPI',
      'HSBK': 'LSIN:HSBK',
      'KAP': 'FWB:0ZQ',
      
      // Основные индексы (TVC)
      'SPX500': 'TVC:SPX',
      'NASDAQ': 'TVC:NDX',
      'DJI': 'TVC:DJI',
      'RUSSELL2000': 'TVC:RUT',
      
      // Европейские индексы
      'DAX': 'TVC:DAX',
      'FTSE': 'TVC:UKX',
      'CAC40': 'TVC:CAC'
    };
    
    return symbolMapping[asset] || 'FX:EURUSD';
  };

  useEffect(() => {
    // Очищаем предыдущий виджет
    if (widgetRef.current) {
      widgetRef.current.innerHTML = '';
    }

    // Создаем уникальный ID для контейнера
    const containerId = `tradingview_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Создаем контейнер для виджета
    const container = document.createElement('div');
    container.id = containerId;
    container.style.height = '100%';
    container.style.width = '100%';
    
    if (widgetRef.current) {
      widgetRef.current.appendChild(container);
    }

    // Загружаем скрипт TradingView если еще не загружен
    if (!window.TradingView) {
      const script = document.createElement('script');
      script.src = 'https://s3.tradingview.com/tv.js';
      script.async = true;
      script.onload = () => {
        initializeWidget();
      };
      document.head.appendChild(script);
      scriptRef.current = script;
    } else {
      initializeWidget();
    }

    function initializeWidget() {
      if (window.TradingView && document.getElementById(containerId)) {
        try {
          new window.TradingView.widget({
            autosize: true,
            symbol: getTradingViewSymbol(asset),
            interval: '5',  // 5-минутный интервал по умолчанию
            timezone: 'Europe/Moscow',
            theme: 'dark',
            style: '1', // Свечи
            locale: 'ru',
            toolbar_bg: '#1f2937', // Темный фон панели инструментов
            enable_publishing: false,
            hide_top_toolbar: false,
            save_image: false,
            hide_legend: false,
            hide_side_toolbar: false,
            allow_symbol_change: false,
            studies: [
              'MASimple@tv-basicstudies',  // Скользящая средняя
            ],
            container_id: containerId,
            // Попытка получения цены из TradingView (ограничения бесплатной версии)
            onChartReady: function() {
              console.log(`TradingView chart loaded for ${asset}`);
              
              // Попытка получить доступ к цене через TradingView API (может не работать в бесплатной версии)
              try {
                if (this.activeChart && this.activeChart.onRealtimeCallback) {
                  this.activeChart.onRealtimeCallback((data) => {
                    if (data && data.close && onPriceUpdate) {
                      console.log(`📈 TradingView price update for ${asset}:`, data.close);
                      onPriceUpdate(asset, data.close);
                    }
                  });
                }
              } catch (error) {
                console.warn(`⚠️ Cannot access price data from TradingView free widget for ${asset}:`, error);
                // Бесплатная версия TradingView не предоставляет доступ к ценам
                if (onPriceUpdate) {
                  onPriceUpdate(asset, null); // Сигнализируем, что цены недоступны
                }
              }
            }
          });
        } catch (error) {
          console.error('Error initializing TradingView widget:', error);
        }
      }
    }

    // Cleanup функция
    return () => {
      if (widgetRef.current) {
        widgetRef.current.innerHTML = '';
      }
    };
  }, [asset]);

  return (
    <div className="w-full h-[600px] lg:h-[700px] bg-gray-900 rounded-lg overflow-hidden">
      <div ref={widgetRef} className="w-full h-full" />
    </div>
  );
};

export default TradingViewWidget;